import Vue from 'vue'

// 实现拖拽排序功能
Vue.directive('draggable', {
  bind: function (el, binding) {
    el.setAttribute('draggable', 'true');
    el._dragData = binding.value;
    
    el.addEventListener('dragstart', (e) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify(el._dragData));
      el.classList.add('dragging');
    });
    
    el.addEventListener('dragend', () => {
      el.classList.remove('dragging');
    });
  }
});

// 实现拖放区域
Vue.directive('drop-zone', {
  bind: function (el, binding) {
    el.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      el.classList.add('drag-over');
    });
    
    el.addEventListener('dragleave', () => {
      el.classList.remove('drag-over');
    });
    
    el.addEventListener('drop', (e) => {
      e.preventDefault();
      el.classList.remove('drag-over');
      
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain'));
        if (binding.value && typeof binding.value === 'function') {
          binding.value(data, el);
        }
      } catch (err) {
        console.error('Drop error:', err);
      }
    });
  }
});

// 关键词匹配游戏组件
Vue.component('keyword-matching-game', {
  props: {
    keywords: {
      type: Array,
      required: true
    },
    descriptions: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: '关键词匹配游戏'
    }
  },
  data() {
    return {
      shuffledDescriptions: [],
      matches: {},
      completed: false,
      score: 0,
      startTime: null,
      endTime: null
    }
  },
  template: `
    <div class="keyword-game-container">
      <h3 class="game-title">{{ title }}</h3>
      
      <div v-if="!completed" class="game-board">
        <div class="game-instructions">
          <p>将右侧的描述拖放到左侧对应的关键词上，完成正确的匹配。</p>
        </div>
        
        <div class="game-content">
          <div class="keywords-column">
            <div 
              v-for="(keyword, index) in keywords" 
              :key="'kw-' + index"
              class="keyword-item"
              :class="{ 'has-match': matches[index] !== undefined }"
              v-drop-zone="(data) => handleDrop(index, data)"
            >
              <div class="keyword-text">{{ keyword }}</div>
              <div v-if="matches[index] !== undefined" class="match-description">
                {{ shuffledDescriptions[matches[index]] }}
                <button class="remove-match" @click="removeMatch(index)">
                  <i class="el-icon-close"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div class="descriptions-column">
            <div 
              v-for="(description, index) in shuffledDescriptions" 
              :key="'desc-' + index"
              class="description-item"
              :class="{ 'is-matched': isDescriptionMatched(index) }"
              v-draggable="{ index: index }"
              v-show="!isDescriptionMatched(index)"
            >
              {{ description }}
            </div>
          </div>
        </div>
        
        <div class="game-actions">
          <el-button type="primary" @click="checkAnswers" :disabled="!isAllMatched">检查答案</el-button>
          <el-button @click="resetGame">重置</el-button>
        </div>
      </div>
      
      <div v-else class="game-results animate__animated animate__fadeIn">
        <div class="result-header">
          <h4>游戏结果</h4>
          <div class="score-display">
            <div class="score-circle" :class="scoreClass">{{ score }}/{{ keywords.length }}</div>
            <p>{{ scoreMessage }}</p>
          </div>
          <div class="time-info">
            <p>完成时间: {{ completionTime }}</p>
          </div>
        </div>
        
        <div class="result-details">
          <div 
            v-for="(keyword, index) in keywords" 
            :key="index"
            class="result-item"
            :class="{ correct: correctMatches[index] === matches[index], incorrect: correctMatches[index] !== matches[index] }"
          >
            <div class="result-keyword">{{ keyword }}</div>
            <div class="result-arrow"><i class="el-icon-arrow-right"></i></div>
            <div class="result-description">
              <div v-if="correctMatches[index] === matches[index]" class="correct-match">
                <i class="el-icon-success"></i>
                <span>{{ descriptions[correctMatches[index]] }}</span>
              </div>
              <div v-else class="incorrect-match">
                <div>
                  <i class="el-icon-error"></i>
                  <span>您的匹配: {{ shuffledDescriptions[matches[index]] }}</span>
                </div>
                <div class="correct-match-text">
                  正确匹配: {{ descriptions[correctMatches[index]] }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="result-actions">
          <el-button type="primary" @click="playAgain">再玩一次</el-button>
        </div>
      </div>
    </div>
  `,
  computed: {
    isAllMatched() {
      return Object.keys(this.matches).length === this.keywords.length;
    },
    correctMatches() {
      // 正确的匹配关系（索引对应）
      const correct = {};
      this.keywords.forEach((keyword, index) => {
        // 找到shuffledDescriptions中对应的原始描述的索引
        const originalIndex = this.descriptions.findIndex(desc => 
          this.shuffledDescriptions.indexOf(desc) !== -1 && 
          this.shuffledDescriptions.indexOf(desc) === this.matches[index]
        );
        correct[index] = originalIndex;
      });
      return correct;
    },
    scoreClass() {
      const percentage = this.score / this.keywords.length;
      if (percentage >= 0.8) return 'excellent';
      if (percentage >= 0.6) return 'good';
      return 'needs-improvement';
    },
    scoreMessage() {
      const percentage = this.score / this.keywords.length;
      if (percentage >= 0.8) return '太棒了！您对关键词有很好的理解！';
      if (percentage >= 0.6) return '不错！您对大部分关键词有了基本的了解。';
      return '继续加油！建议您重新学习关键词相关内容。';
    },
    completionTime() {
      if (!this.startTime || !this.endTime) return '0秒';
      const seconds = Math.floor((this.endTime - this.startTime) / 1000);
      if (seconds < 60) return `${seconds}秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}分${remainingSeconds}秒`;
    }
  },
  methods: {
    shuffleArray(array) {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    },
    handleDrop(keywordIndex, data) {
      if (data && data.index !== undefined) {
        // 如果这个描述已经被匹配了，先移除之前的匹配
        Object.keys(this.matches).forEach(kwIndex => {
          if (this.matches[kwIndex] === data.index) {
            this.$delete(this.matches, kwIndex);
          }
        });
        
        // 添加新的匹配
        this.$set(this.matches, keywordIndex, data.index);
        
        // 如果是第一次匹配，记录开始时间
        if (!this.startTime) {
          this.startTime = new Date();
        }
      }
    },
    removeMatch(keywordIndex) {
      this.$delete(this.matches, keywordIndex);
    },
    isDescriptionMatched(descIndex) {
      return Object.values(this.matches).includes(descIndex);
    },
    checkAnswers() {
      this.score = 0;
      this.keywords.forEach((keyword, index) => {
        if (this.correctMatches[index] === this.matches[index]) {
          this.score++;
        }
      });
      
      this.endTime = new Date();
      this.completed = true;
      
      // 触发完成事件
      this.$emit('completed', {
        score: this.score,
        total: this.keywords.length,
        time: this.completionTime
      });
    },
    resetGame() {
      this.matches = {};
      this.shuffledDescriptions = this.shuffleArray(this.descriptions);
    },
    playAgain() {
      this.resetGame();
      this.completed = false;
      this.score = 0;
      this.startTime = null;
      this.endTime = null;
    },
    init() {
      this.shuffledDescriptions = this.shuffleArray(this.descriptions);
      this.matches = {};
      this.completed = false;
      this.score = 0;
      this.startTime = null;
      this.endTime = null;
    }
  },
  mounted() {
    this.init();
  }
});

// 添加全局样式
const style = document.createElement('style');
style.innerHTML = `
  .keyword-game-container {
    background-color: #f5f7fa;
    border-radius: 10px;
    padding: 25px;
    margin: 30px 0;
  }
  
  .dark-mode .keyword-game-container {
    background-color: #1a1a2e;
  }
  
  .game-title {
    font-size: 20px;
    margin-bottom: 20px;
    color: #7957d5;
  }
  
  .game-instructions {
    margin-bottom: 20px;
    padding: 10px;
    background-color: rgba(121, 87, 213, 0.1);
    border-radius: 5px;
  }
  
  .dark-mode .game-instructions {
    background-color: rgba(121, 87, 213, 0.2);
  }
  
  .game-content {
    display: flex;
    gap: 30px;
    margin-bottom: 30px;
  }
  
  .keywords-column, .descriptions-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .keyword-item {
    background-color: white;
    border-radius: 5px;
    padding: 15px;
    min-height: 60px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .dark-mode .keyword-item {
    background-color: #16213e;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  .keyword-item.has-match {
    border: 2px solid #7957d5;
  }
  
  .keyword-item.drag-over {
    background-color: rgba(121, 87, 213, 0.1);
    transform: scale(1.02);
  }
  
  .dark-mode .keyword-item.drag-over {
    background-color: rgba(121, 87, 213, 0.2);
  }
  
  .keyword-text {
    font-weight: bold;
    color: #333;
  }
  
  .dark-mode .keyword-text {
    color: #f0f0f0;
  }
  
  .match-description {
    background-color: #7957d5;
    color: white;
    padding: 10px;
    border-radius: 5px;
    position: relative;
  }
  
  .remove-match {
    position: absolute;
    top: 5px;
    right: 5px;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 16px;
    opacity: 0.7;
    transition: opacity 0.3s;
  }
  
  .remove-match:hover {
    opacity: 1;
  }
  
  .description-item {
    background-color: #3273dc;
    color: white;
    padding: 15px;
    border-radius: 5px;
    cursor: grab;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }
  
  .description-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  .dark-mode .description-item:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  .description-item.dragging {
    opacity: 0.5;
  }
  
  .description-item.is-matched {
    display: none;
  }
  
  .game-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
  }
  
  .result-item {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    padding: 15px;
    border-radius: 5px;
  }
  
  .result-item.correct {
    background-color: rgba(103, 194, 58, 0.1);
  }
  
  .result-item.incorrect {
    background-color: rgba(245, 108, 108, 0.1);
  }
  
  .result-keyword {
    font-weight: bold;
    margin-right: 15px;
    min-width: 100px;
  }
  
  .result-arrow {
    margin: 0 15px;
    color: #999;
  }
  
  .correct-match {
    color: #67c23a;
    display: flex;
    align-items: center;
  }
  
  .incorrect-match {
    color: #f56c6c;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  
  .correct-match i, .incorrect-match i {
    margin-right: 10px;
  }
  
  .correct-match-text {
    margin-top: 5px;
    color: #67c23a;
  }
  
  .dragging {
    cursor: grabbing;
  }
`;
document.head.appendChild(style);
